import { Injectable } from '@angular/core';
import { forkJoin } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import {
  Rol,
  Usuario,
  ParametrosGenerales,
  Personalizacion,
  PERMISOS_DEF,
  ROLES_INICIALES,
} from '../models/configuracion.model';
import { DatabaseService } from './database.service';
import { environment } from '../../../environments/environment';

const PARAMETROS_DEFAULT: ParametrosGenerales = {
  nombreEmpresa: 'Restora360',
  rut: '',
  notificacionesEmail: true,
  notificacionesPush: false,
  maxIntentosLogin: 5,
  bloqueoMinutos: 15,
};

const PERSONALIZACION_DEFAULT: Personalizacion = {
  tema: 'claro',
  idioma: 'es',
  formatoFecha: 'DD/MM/YYYY',
  zonaHoraria: 'America/Bogota',
  filasPorPagina: 10,
};

const USUARIOS_INICIALES: Usuario[] = [
  { id: '1', nombre: 'Administrador', email: 'admin@restora360.com', rolId: 'admin', activo: true, createdAt: '2024-01-01T00:00:00', updatedAt: '2024-01-01T00:00:00' },
  { id: '2', nombre: 'Juan Mesero', email: 'mesero@restora360.com', rolId: 'mesero', activo: true, createdAt: '2024-01-01T00:00:00', updatedAt: '2024-01-01T00:00:00' },
  { id: '3', nombre: 'Carlos Cocinero', email: 'cocinero@restora360.com', rolId: 'cocinero', activo: true, createdAt: '2024-01-01T00:00:00', updatedAt: '2024-01-01T00:00:00' },
  { id: '4', nombre: 'María Cajero', email: 'cajero@restora360.com', rolId: 'cajero', activo: true, createdAt: '2024-01-01T00:00:00', updatedAt: '2024-01-01T00:00:00' },
];

const CONFIG_METADATA_KEYS = {
  roles: 'config_roles',
  usuarios: 'config_usuarios',
  parametros: 'config_parametros',
  personalizacion: 'config_personalizacion',
};

@Injectable({
  providedIn: 'root',
})
export class ConfiguracionService {
  private roles: Rol[] = ROLES_INICIALES.map(r => ({ ...r, permisos: [...r.permisos] }));
  private parametros: ParametrosGenerales = { ...PARAMETROS_DEFAULT };
  private personalizacion: Personalizacion = { ...PERSONALIZACION_DEFAULT };
  private usuarios: Usuario[] = USUARIOS_INICIALES.map(u => ({ ...u }));

  constructor(private database: DatabaseService) {}

  /** Carga roles, usuarios, parámetros y personalización desde IndexedDB (modo demo). Debe llamarse al arranque. */
  loadFromIndexedDB(): Promise<void> {
    if (!environment.demo) return Promise.resolve();

    return new Promise((resolve, reject) => {
      forkJoin({
        roles: this.database.getMetadata(CONFIG_METADATA_KEYS.roles),
        usuarios: this.database.getMetadata(CONFIG_METADATA_KEYS.usuarios),
        parametros: this.database.getMetadata(CONFIG_METADATA_KEYS.parametros),
        personalizacion: this.database.getMetadata(CONFIG_METADATA_KEYS.personalizacion),
      })
        .pipe(
          tap(({ roles, usuarios, parametros, personalizacion }) => {
            if (roles?.value && Array.isArray(roles.value) && roles.value.length > 0) {
              this.roles = roles.value.map((r: Rol) => ({ ...r, permisos: [...(r.permisos || [])] }));
            }
            if (usuarios?.value && Array.isArray(usuarios.value)) {
              this.usuarios = usuarios.value.map((u: Usuario) => ({ ...u }));
            }
            if (parametros?.value && typeof parametros.value === 'object') {
              this.parametros = { ...PARAMETROS_DEFAULT, ...parametros.value };
            }
            if (personalizacion?.value && typeof personalizacion.value === 'object') {
              this.personalizacion = { ...PERSONALIZACION_DEFAULT, ...personalizacion.value };
            }
          }),
          catchError(err => {
            reject(err);
            throw err;
          })
        )
        .subscribe({
          next: () => resolve(),
          error: err => reject(err),
        });
    });
  }

  /** Persiste la configuración actual en IndexedDB (modo demo). */
  private persistConfig(): void {
    if (!environment.demo) return;

    forkJoin({
      roles: this.database.setMetadata(CONFIG_METADATA_KEYS.roles, this.getRoles()),
      usuarios: this.database.setMetadata(CONFIG_METADATA_KEYS.usuarios, this.getUsers()),
      parametros: this.database.setMetadata(CONFIG_METADATA_KEYS.parametros, this.getParametros()),
      personalizacion: this.database.setMetadata(CONFIG_METADATA_KEYS.personalizacion, this.getPersonalizacion()),
    }).subscribe({
      error: err => console.error('Error persistiendo configuración en IndexedDB:', err),
    });
  }

  getRoles(): Rol[] {
    return this.roles.map(r => ({ ...r, permisos: [...r.permisos] }));
  }

  getRolById(id: string): Rol | undefined {
    return this.roles.find(r => r.id === id);
  }

  /** Devuelve la lista de claves de permisos para un rol (admin, mesero, cocinero, cajero). */
  getPermisosForRol(rolId: string): string[] {
    const rol = this.getRolById(rolId);
    return rol ? [...rol.permisos] : [];
  }

  /** Indica si el rol tiene asignado el permiso. Compatible con permisos legacy (configuracion_usuarios, configuracion_roles). */
  hasPermiso(rolId: string, permisoClave: string): boolean {
    const permisos = this.getPermisosForRol(rolId);
    if (permisos.includes(permisoClave)) return true;
    if (permisoClave.startsWith('configuracion_usuarios_') && permisos.includes('configuracion_usuarios')) return true;
    if (permisoClave.startsWith('configuracion_roles_') && permisos.includes('configuracion_roles')) return true;
    return false;
  }

  getPermisosDef() {
    return PERMISOS_DEF;
  }

  getParametros(): ParametrosGenerales {
    return { ...this.parametros };
  }

  updateParametros(dto: Partial<ParametrosGenerales>): void {
    this.parametros = { ...this.parametros, ...dto };
    this.persistConfig();
  }

  getPersonalizacion(): Personalizacion {
    return { ...this.personalizacion };
  }

  updatePersonalizacion(dto: Partial<Personalizacion>): void {
    this.personalizacion = { ...this.personalizacion, ...dto };
    this.persistConfig();
  }

  /** IDs de roles del sistema que no se pueden eliminar. */
  private readonly SYSTEM_ROLE_IDS = ['admin', 'mesero', 'cocinero', 'cajero'];

  isSystemRole(id: string): boolean {
    return this.SYSTEM_ROLE_IDS.includes(id);
  }

  createRol(dto: { nombre: string; descripcion: string; permisos?: string[] }): Rol {
    const id = 'rol_' + Date.now();
    const permisos = dto.permisos ?? [];
    const rol: Rol = {
      id,
      nombre: dto.nombre.trim(),
      descripcion: dto.descripcion.trim(),
      permisos: [...permisos],
    };
    this.roles.push(rol);
    this.persistConfig();
    return { ...rol };
  }

  updateRol(id: string, dto: { nombre?: string; descripcion?: string; permisos?: string[] }): void {
    const index = this.roles.findIndex(r => r.id === id);
    if (index === -1) return;
    const current = this.roles[index];
    if (dto.nombre !== undefined) current.nombre = dto.nombre.trim();
    if (dto.descripcion !== undefined) current.descripcion = dto.descripcion.trim();
    if (dto.permisos !== undefined) current.permisos = [...dto.permisos];
    this.roles[index] = { ...current };
    this.persistConfig();
  }

  /** Elimina un rol. No permite eliminar roles del sistema (admin, mesero, cocinero, cajero). */
  deleteRol(id: string): boolean {
    if (this.isSystemRole(id)) return false;
    const index = this.roles.findIndex(r => r.id === id);
    if (index === -1) return false;
    this.roles.splice(index, 1);
    this.persistConfig();
    return true;
  }

  getUsers(): Usuario[] {
    return this.usuarios.map(u => ({ ...u }));
  }

  createUser(dto: Omit<Usuario, 'id' | 'createdAt' | 'updatedAt'>): Usuario {
    const now = new Date().toISOString();
    const id = 'usr_' + Date.now();
    const user: Usuario = {
      id,
      nombre: dto.nombre,
      email: dto.email,
      rolId: dto.rolId,
      activo: dto.activo ?? true,
      createdAt: now,
      updatedAt: now,
    };
    this.usuarios.push(user);
    this.persistConfig();
    return { ...user };
  }

  updateUser(id: string, dto: Partial<Pick<Usuario, 'nombre' | 'email' | 'rolId' | 'activo'>>): void {
    const index = this.usuarios.findIndex(u => u.id === id);
    if (index !== -1) {
      const updated = { ...this.usuarios[index], ...dto, updatedAt: new Date().toISOString() };
      this.usuarios[index] = updated;
      this.persistConfig();
    }
  }

  deleteUser(id: string): boolean {
    const index = this.usuarios.findIndex(u => u.id === id);
    if (index === -1) return false;
    this.usuarios.splice(index, 1);
    this.persistConfig();
    return true;
  }

  getUsuarioById(id: string): Usuario | undefined {
    return this.usuarios.find(u => u.id === id);
  }
}
